import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MUHASEBE_AD, api, formatAy, formatKisa, formatSayi, formatTarih, formatTl } from '../lib/api';
import { CHANNEL_LABEL, MOVE_REASON_LABEL, type MoveReason, type StockChannel } from '@artmozaik/shared';
import { useAuth } from '../lib/auth';
import KpiKart, { KpiIzgara, Ikon, type IkonAd } from '../ui/KpiKart';
import { CubukGrafik, SiraliCubuk } from '../ui/Grafik';
import DonemSecici, { donemYazi, useDonem } from '../ui/DonemSecici';
import { Kutu, RaporGezgin } from '../ui/Rapor';
import Ipucu from '../ui/Ipucu';

/**
 * GENEL ÖZET — gösterge paneli.
 *
 * Parayı görebilen rolde (receivable.view): dönem çubuğu, sekiz KPI kartı,
 * aylık ciro/alış grafiği, sipariş hattı, dikkat gerekenler, ilk 5 müşteri.
 * Diğer rollerde yalnız operasyon kartları (kritik stok, teklif, fason).
 * Rakamların tanımı fihristtedir (`kilavuz/fihrist.ts`, kartlardaki «?»); hesap `/api/rapor/ozet`te.
 */
type Veri = {
  kpi: {
    kritikStokAdedi: number; acikAlacakKurus?: number; teklifAy: number; teklifDonusPct: number | null; fasonBekleyen: number;
    /** Kasa + banka toplamı, para birimi bazında. Yalnız `cash.view` rolüne gelir; gayriresmî hesaplar yalnız görebilene dahildir. */
    kasaBanka?: { para: string; kurus: number }[];
    /** Vadesi yaklaşan (uyarı günü içinde) ve geçmiş bekleyen çek / senetler. Yalnız `cheque.view` rolüne gelir. */
    cekVade?: { gun: number; adet: number; gecmisAdet: number; giris: { para: string; kurus: number }[]; cikis: { para: string; kurus: number }[] };
    /** Bu ayın kredi taksitleri, para birimi bazında. Yalnız `loan.view` rolüne gelir. */
    krediAy?: { para: string; toplamKurus: number; adet: number; kalanKurus: number; kalanAdet: number }[];
  };
  uyari: { type: string; payload: Record<string, unknown>; at: string } | null;
  /** offrec null ise bu rol faturasız haneyi görmüyor — kolon hiç basılmaz. */
  kritikStok: { id: number; code: string; name: string; official: number; offrec: number | null; threshold: number }[];
  sonHareketler: { id: number; at: string; code: string; name: string; channel: StockChannel; qty: number; reason: MoveReason; note: string | null; by: string }[];
};
type Toplam = { adet: number; tutarKurus: number };
type Ozet = {
  donem: { bas: string; bit: string; gun: number; onceki: { bas: string; bit: string } };
  ciro: { haricKurus: number; dahilKurus: number; adet: number; iadeAdet: number; oncekiKurus: number; oncekiAdet: number; degisimPct: number | null };
  alis: { haricKurus: number; adet: number; oncekiKurus: number; degisimPct: number | null };
  fark: { kurus: number; oncekiKurus: number; degisimPct: number | null };
  siparis: Toplam & { tutarsiz: number; oncekiAdet: number; oncekiKurus: number; degisimPct: number | null };
  acikSiparis: Toplam & { tutarsiz: number };
  tahsilEdilmemis: { faturaKurus: number; faturaAdet: number; faturasizKurus: number | null; faturasizAdet: number | null };
  vadesiGecen: Toplam & { fatura: Toplam; siparis: Toplam };
  depo: { toplamMaliyetKurus: number; stokluKalem: number; maliyetiBilinmeyen: number } | null;
  dikkat: { esikAlti: number; vadesiGecen: number; onayBekleyenTeklif: number; fasonBekleyen: number; takipsizCari: number; acikDestek: number };
  ilkMusteriler: { id: number | null; name: string; ciroKurus: number; faturaAdet: number }[];
  asamalar: ({ id: string; ad: string; tutarsiz: number } & Toplam)[];
};
type Trend = { aylar: { ay: string; ciroHaricKurus: number; alisHaricKurus: number; satisAdet: number; alisAdet: number; siparisAdet: number }[] };

/** "Cumartesi · 12 Eylül 2026" — gün adı önce. */
const bugun = () => {
  const d = new Date();
  const tz = 'Europe/Istanbul';
  const gun = new Intl.DateTimeFormat('tr-TR', { weekday: 'long', timeZone: tz }).format(d);
  const tarih = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: tz }).format(d);
  return `${gun} · ${tarih}`;
};
const eksen = (v: number) => formatKisa(v, { eksen: true });

export default function Dashboard() {
  const { me, can } = useAuth();
  const paraGorur = can('receivable.view');
  const { donem, ayarla, sorgu } = useDonem();

  const [veri, setVeri] = useState<Veri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [ozet, setOzet] = useState<Ozet | null>(null);
  const [trend, setTrend] = useState<Trend | null>(null);
  const [ozetHata, setOzetHata] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  /** Dönem bandı: açılması gereken yeni dönem ve kapanışa hazır dönemler (yalnız `period.view` rolüne). */
  const [donemBant, setDonemBant] = useState<{ acilacakYil: number | null; kapatilacak: number[]; yonet: boolean } | null>(null);
  const donemGorur = can('period.view');
  useEffect(() => { if (donemGorur) api<NonNullable<typeof donemBant>>('/api/donem/bant').then(setDonemBant).catch(() => undefined); }, [donemGorur]);

  const yukle = useCallback(() => {
    setYukleniyor(true);
    const isler: Promise<unknown>[] = [api<Veri>('/api/dashboard').then(setVeri).catch((e) => setHata((e as Error).message))];
    if (paraGorur) {
      setOzetHata(null);
      isler.push(api<Ozet>(`/api/rapor/ozet?${sorgu}`).then(setOzet).catch((e) => setOzetHata((e as Error).message)));
      isler.push(api<Trend>('/api/rapor/trend?ay=12').then(setTrend).catch(() => undefined));
    }
    void Promise.all(isler).finally(() => setYukleniyor(false));
  }, [paraGorur, sorgu]);
  useEffect(yukle, [yukle]);

  if (hata) {
    return (
      <div className="ccd-banner ccd-banner-danger">
        <span className="ccd-banner-icon" aria-hidden="true">!</span>
        <span>{hata}</span>
      </div>
    );
  }
  if (!veri) return <div className="ccd-inline-note">Yükleniyor…</div>;

  const { kpi } = veri;
  // Operasyon kartları: 3 sabit + role göre "Bekleyen tahsilat" ve "Kasa ve banka".
  const kartSayisi = 3 + (kpi.acikAlacakKurus != null ? 1 : 0) + (kpi.kasaBanka ? 1 : 0);
  const kasaTl = kpi.kasaBanka?.find((x) => x.para === 'TRY')?.kurus ?? 0;
  const kasaDoviz = (kpi.kasaBanka ?? []).filter((x) => x.para !== 'TRY' && x.kurus !== 0)
    .map((x) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: x.para, maximumFractionDigits: 2 }).format(x.kurus / 100)).join(' · ');
  const paraSatiri = (l: { para: string; kurus: number }[]) => l.filter((x) => x.kurus !== 0)
    .map((x) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: x.para, maximumFractionDigits: 0 }).format(x.kurus / 100)).join(' + ');
  const krediTl = kpi.krediAy?.find((x) => x.para === 'TRY');
  const krediDoviz = paraSatiri((kpi.krediAy ?? []).filter((x) => x.para !== 'TRY').map((x) => ({ para: x.para, kurus: x.toplamKurus })));
  const faturasizVar = veri.kritikStok.some((r) => r.offrec !== null);
  const oncekiYazi = (deger: string) => (ozet ? `Önceki dönem (${donemYazi(ozet.donem.onceki)}): ${deger}` : undefined);
  const dikkat: { ad: string; adet: number; to: string; ikon: IkonAd; aciklama: string; fihrist: string }[] = ozet ? [
    { ad: 'Eşik altı stok', adet: ozet.dikkat.esikAlti, to: '/stok', ikon: 'kutu', aciklama: 'kalem eşiğinin altında', fihrist: 'esik' },
    { ad: 'Vadesi geçen ödeme', adet: ozet.dikkat.vadesiGecen, to: `/rapor/tahsilat-riski?${sorgu}`, ikon: 'uyari', aciklama: 'fatura ya da sipariş ödemesi', fihrist: 'vadesi-gecen' },
    { ad: 'Onay bekleyen teklif', adet: ozet.dikkat.onayBekleyenTeklif, to: '/teklif', ikon: 'onay', aciklama: 'iskonto tavanını aşıyor', fihrist: 'onay-bekleyen-teklif' },
    { ad: 'Fasonda bekleyen', adet: ozet.dikkat.fasonBekleyen, to: '/fason', ikon: 'fabrika', aciklama: 'iş fasoncuda', fihrist: 'fason-bekleyen' },
    { ad: 'Takipsiz cari', adet: ozet.dikkat.takipsizCari, to: '/crm', ikon: 'kisi', aciklama: 'uzun süredir temas yok', fihrist: 'takipsiz-cari' },
    { ad: 'Açık destek talebi', adet: ozet.dikkat.acikDestek, to: '/destek', ikon: 'belge', aciklama: 'yanıt bekliyor', fihrist: 'acik-destek' },
  ] : [];

  return (
    <>
      {paraGorur && <RaporGezgin sorgu={sorgu} />}
      <div className="ccd-rapor-baslik">
        <div style={{ minWidth: 0 }}>
          <div className="ccd-eyebrow">{bugun()}</div>
          <h1 className="ccd-title">Genel Özet</h1>
          <div className="ccd-meta ccd-rapor-aciklama">
            {me ? `Merhaba ${me.name.split(' ')[0]}` : 'Merhaba'} — {kpi.kritikStokAdedi} kalem eşik altında
            {paraGorur && ` · dönem: ${donemYazi(donem)}`}
          </div>
        </div>
        <div className="ccd-row ccd-rapor-dugmeler">
          <button className="ccd-btn ccd-btn-sm" onClick={() => window.print()} title="Tarayıcının yazdırma penceresi açılır; hedef olarak PDF seçebilirsiniz">Yazdır / PDF</button>
          <button className="ccd-btn ccd-btn-sm" onClick={yukle} disabled={yukleniyor} title="Rakamları yeniden çek">{yukleniyor ? 'Yükleniyor…' : 'Yenile'}</button>
        </div>
      </div>

      {veri.uyari && (
        <div className="ccd-banner ccd-banner-warning">
          <span className="ccd-banner-icon" aria-hidden="true">₺</span>
          <div>
            <div style={{ fontWeight: 600 }}>Alış fiyatı arttı</div>
            <div className="ccd-meta">
              {[veri.uyari.payload.code, veri.uyari.payload.aciklama].filter(Boolean).join(' · ')}
              {veri.uyari.payload.artisYuzde != null ? ` · %${String(veri.uyari.payload.artisYuzde)}` : ''} · {formatTarih(veri.uyari.at)}
            </div>
          </div>
        </div>
      )}

      {donemBant && (donemBant.acilacakYil != null || donemBant.kapatilacak.length > 0) && (
        <div className="ccd-banner ccd-banner-warning ccd-donemk-bant" data-fihrist="donem-yil">
          <span className="ccd-banner-icon" aria-hidden="true">↻</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>
              {donemBant.acilacakYil != null ? `${donemBant.acilacakYil} dönemini açın` : `${donemBant.kapatilacak.join(', ')} dönemi kapanışa hazır`}
            </div>
            <div className="ccd-meta">
              {donemBant.acilacakYil != null && `Yıl değişiyor: ${donemBant.acilacakYil} dönemi tek tuşla açılır. Kayıtlarınız silinmez, taşınmaz. `}
              {donemBant.kapatilacak.length > 0 && `${donemBant.kapatilacak.join(', ')} yılı bitti; son kayıtları girdikten sonra dönemi kapatın. `}
              {!donemBant.yonet && 'Dönemi yönetici ya da operasyon açar ve kapatır. '}
              <Link className="ccd-link" to="/donem">Dönemler ekranına git →</Link>
            </div>
          </div>
        </div>
      )}

      {paraGorur && <DonemSecici donem={donem} ayarla={ayarla} />}
      {paraGorur && ozetHata && (
        <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{ozetHata}</span></div>
      )}

      {paraGorur && ozet && (
        <>
          <KpiIzgara>
            <KpiKart ton="yesil" ikon="para" etiket="Ciro" fihrist="ciro" deger={formatKisa(ozet.ciro.haricKurus)} tam={formatTl(ozet.ciro.haricKurus)} to={`/rapor/satis?${sorgu}`}
                     ipucu="Dönemdeki satış faturalarının KDV hariç, iskonto sonrası toplamı. İptal fatura sayılmaz, iade düşülür."
                     degisim={{ pct: ozet.ciro.degisimPct, onceki: oncekiYazi(formatKisa(ozet.ciro.oncekiKurus)) }}
                     alt={<div>{ozet.ciro.adet} fatura · {formatTl(ozet.ciro.haricKurus)}</div>} />
            <KpiKart ton="mavi" ikon="sepet" etiket="Sipariş" fihrist="siparis-sayisi" deger={formatSayi(ozet.siparis.adet)} to={`/rapor/siparisler?${sorgu}`}
                     ipucu="Dönemde açılan, iptal edilmemiş sipariş sayısı. Tutar siparişin teklifinden gelir (KDV dahil); teklifsiz siparişin tutarı yoktur."
                     degisim={{ pct: ozet.siparis.degisimPct, onceki: oncekiYazi(`${ozet.siparis.oncekiAdet} sipariş`) }}
                     alt={<div title={formatTl(ozet.siparis.tutarKurus)}>{formatKisa(ozet.siparis.tutarKurus)} tutarında</div>} />
            <KpiKart ton="turuncu" ikon="saat" etiket="Açık sipariş" fihrist="acik-siparis" deger={formatSayi(ozet.acikSiparis.adet)} to="/siparis" soluk={ozet.acikSiparis.adet === 0}
                     ipucu="Henüz kapanmamış ve iptal edilmemiş bütün siparişler (bugünkü durum, dönemden bağımsız). Tutar KDV dahil."
                     alt={<div title={formatTl(ozet.acikSiparis.tutarKurus)}>{formatKisa(ozet.acikSiparis.tutarKurus)}{ozet.acikSiparis.tutarsiz > 0 ? ` · ${ozet.acikSiparis.tutarsiz} tanesinin tutarı yok` : ''}</div>} />
            <KpiKart ton="mor" ikon="cuzdan" etiket="Tahsil edilmemiş" fihrist="tahsil-edilmemis" deger={formatKisa(ozet.tahsilEdilmemis.faturaKurus)} tam={formatTl(ozet.tahsilEdilmemis.faturaKurus)}
                     to={`/rapor/tahsilat-riski?${sorgu}`} soluk={ozet.tahsilEdilmemis.faturaKurus === 0}
                     ipucu={`${MUHASEBE_AD}'te kalanı görünen bütün satış faturalarının toplamı (bugünkü durum, KDV dahil). Faturasız açık alacak ayrı satırdadır, bu rakama dahil değildir.`}
                     alt={<>
                       <div>{ozet.tahsilEdilmemis.faturaAdet} fatura · {formatTl(ozet.tahsilEdilmemis.faturaKurus)}</div>
                       {ozet.tahsilEdilmemis.faturasizKurus != null && <div>Faturasız açık alacak: {formatTl(ozet.tahsilEdilmemis.faturasizKurus)}</div>}
                     </>} />

            <KpiKart ton="turuncu" ikon="kutu" etiket="Alış" fihrist="alis-toplami" deger={formatKisa(ozet.alis.haricKurus)} tam={formatTl(ozet.alis.haricKurus)} to={`/rapor/finans?${sorgu}`}
                     ipucu="Dönemdeki alış ve gider faturalarının KDV hariç toplamı."
                     degisim={{ pct: ozet.alis.degisimPct, onceki: oncekiYazi(formatKisa(ozet.alis.oncekiKurus)), artisKotu: true }}
                     alt={<div>{ozet.alis.adet} fatura · {formatTl(ozet.alis.haricKurus)}</div>} />
            <KpiKart ton={ozet.fark.kurus >= 0 ? 'yesil' : 'kirmizi'} ikon="terazi" etiket="Ciro − alış" fihrist="ciro-eksi-alis" deger={formatKisa(ozet.fark.kurus)} tam={formatTl(ozet.fark.kurus)}
                     to={`/rapor/finans?${sorgu}`}
                     ipucu="Ciro eksi alış faturaları (ikisi de KDV hariç). KÂR DEĞİLDİR: stok değişimi, personel ve fatura dışı giderler hesaba girmez."
                     alt={<div>{formatTl(ozet.fark.kurus)}</div>} />
            <KpiKart ton="kirmizi" ikon="uyari" etiket="Vadesi geçen" fihrist="vadesi-gecen" deger={formatKisa(ozet.vadesiGecen.tutarKurus)} tam={formatTl(ozet.vadesiGecen.tutarKurus)} tur="vade-uyari"
                     to={`/rapor/tahsilat-riski?${sorgu}`} soluk={ozet.vadesiGecen.adet === 0} vurgu={ozet.vadesiGecen.adet > 0}
                     ipucu="Vadesi geçmiş satış faturaları ile sipariş ödeme planındaki gecikmiş parçaların kalan toplamı (bugünkü durum). Ödeme planlı siparişe bağlı fatura iki kez sayılmaz."
                     alt={<>
                       <div>{ozet.vadesiGecen.adet} ödeme · {formatTl(ozet.vadesiGecen.tutarKurus)}</div>
                       <div>Fatura {ozet.vadesiGecen.fatura.adet} · sipariş planı {ozet.vadesiGecen.siparis.adet}</div>
                     </>} />
            {ozet.depo ? (
              <KpiKart ton="mor" ikon="depo" etiket="Depo stok maliyeti" fihrist="depo-stok-degeri" deger={formatKisa(ozet.depo.toplamMaliyetKurus)} tam={formatTl(ozet.depo.toplamMaliyetKurus)}
                       to={`/rapor/stok?${sorgu}`}
                       ipucu="Stok × birim maliyet (bugünkü durum). Maliyeti bilinmeyen kalem sıfır sayılmaz; toplama hiç girmez."
                       alt={<div>{ozet.depo.stokluKalem} stoklu kalem{ozet.depo.maliyetiBilinmeyen > 0 ? ` · ${ozet.depo.maliyetiBilinmeyen} kalemin maliyeti bilinmiyor` : ''}</div>} />
            ) : (
              <KpiKart ton="gri" ikon="kutu" etiket="Eşik altı stok" fihrist="esik" deger={formatSayi(ozet.dikkat.esikAlti)} to="/stok"
                       ipucu="Stoğu kendi eşiğinin altına inmiş aktif ürün sayısı." />
            )}
          </KpiIzgara>

          <div className="ccd-rapor-iki ccd-rapor-iki-genis">
            <Kutu fihrist="aylik-ciro-alis" baslik="Aylık ciro ve alış" not="Son 12 ay · KDV hariç · dönem seçiminden bağımsız" sag={<Link to={`/rapor/finans?${sorgu}`} className="ccd-meta">Finans raporu →</Link>}>
              {trend
                ? <CubukGrafik ad="Aylık ciro ve alış, son 12 ay" eksen={eksen} tam={formatTl}
                               seriler={[{ ad: 'Ciro', ton: 'yesil' }, { ad: 'Alış', ton: 'turuncu' }]}
                               veri={trend.aylar.map((a) => ({ etiket: formatAy(a.ay), baslik: `${formatAy(a.ay)} · ${a.satisAdet} satış, ${a.alisAdet} alış faturası`, degerler: [a.ciroHaricKurus, a.alisHaricKurus] }))} />
                : <div className="ccd-inline-note">Yükleniyor…</div>}
            </Kutu>
            <Kutu fihrist="siparis-asamalari" baslik="Sipariş hattı" not="Hangi aşamada kaç sipariş bekliyor" sag={<Link to="/siparis" className="ccd-meta">Aç →</Link>}>
              <SiraliCubuk ad="Aşamalara göre sipariş" ton="mavi" yaz={(n) => `${formatSayi(n)} sipariş`} bos="Açık sipariş yok."
                           satirlar={ozet.asamalar.filter((a) => a.adet > 0 && a.id !== 'KAPANDI').map((a) => ({ ad: a.ad, deger: a.adet, to: '/siparis',
                             alt: `${formatTl(a.tutarKurus)}${a.tutarsiz > 0 ? ` · ${a.tutarsiz} teklifsiz` : ''}` }))} />
            </Kutu>
          </div>

          <div className="ccd-rapor-iki">
            <Kutu fihrist="dikkat-gerekenler" baslik="Dikkat gerekenler" not="Bugünkü durum · tıklayınca ilgili ekran açılır">
              <div className="ccd-dikkat">
                {dikkat.map((d) => (
                  <Link key={d.ad} to={d.to} className={`ccd-dikkat-satir${d.adet === 0 ? ' ccd-kpi-soluk' : ''}`} data-fihrist={d.fihrist}>
                    <span className={`ccd-dikkat-ikon ccd-ton-${d.adet === 0 ? 'gri' : d.ikon === 'uyari' ? 'kirmizi' : 'turuncu'}`}><Ikon ad={d.ikon} boyut={16} /></span>
                    <span className="ccd-dikkat-ad">{d.ad}<span className="ccd-meta"> · {d.adet === 0 ? 'yok' : d.aciklama}</span></span>
                    <b>{formatSayi(d.adet)}</b>
                  </Link>
                ))}
              </div>
            </Kutu>
            <Kutu fihrist="ilk-musteriler" baslik="İlk 5 müşteri" not={`Dönem cirosu · ${donemYazi(donem)}`} sag={<Link to={`/rapor/musteriler?${sorgu}`} className="ccd-meta">Tümü →</Link>}>
              <SiraliCubuk ad="İlk 5 müşteri" ton="yesil" yaz={formatKisa} tam={formatTl} bos="Bu dönemde satış faturası yok."
                           satirlar={ozet.ilkMusteriler.map((c) => ({ ad: c.name, deger: c.ciroKurus, to: c.id ? `/crm/${c.id}` : undefined, alt: `${c.faturaAdet} fatura` }))} />
            </Kutu>
          </div>
        </>
      )}

      {/* Operasyon kartları — her rolde. Kılavuz turu bu sıraya bağlı (data-tur="kpi"). */}
      <KpiIzgara tur="kpi" uclu={kartSayisi === 3} besli={kartSayisi === 5}>
        <KpiKart ton="kirmizi" ikon="kutu" etiket="Kritik stok" fihrist="esik" deger={`${formatSayi(kpi.kritikStokAdedi)} kalem`} to="/stok" soluk={kpi.kritikStokAdedi === 0}
                 ipucu="Stoğu kendi eşiğinin altına inmiş aktif ürün sayısı."
                 alt={<div>{veri.kritikStok[0]
                   ? <>En düşük: {veri.kritikStok[0].name} → <b>{veri.kritikStok[0].official + (veri.kritikStok[0].offrec ?? 0)}</b></>
                   : 'Eşik altında kalem yok'}</div>} />
        {/* Açık alacak yalnız tahsilatı görebilene gelir. */}
        {kpi.acikAlacakKurus != null && (
          <KpiKart ton="mor" ikon="cuzdan" etiket="Bekleyen tahsilat" fihrist="faturasiz-alacak" deger={formatKisa(kpi.acikAlacakKurus)} tam={formatTl(kpi.acikAlacakKurus)} to="/tahsilat"
                   ipucu="Faturasız çıkışlardan açılan, henüz kapanmamış alacakların kalanı." soluk={kpi.acikAlacakKurus === 0}
                   alt={<div>Faturasız çıkışlardan · {formatTl(kpi.acikAlacakKurus)}</div>} />
        )}
        {/* Kasa ve banka yalnız kasayı görebilene gelir; gayriresmî hesapları göremeyen rolde toplam yalnız resmî hesaplardır. */}
        {kpi.kasaBanka && (
          <KpiKart ton="yesil" ikon="cuzdan" etiket="Kasa ve banka" fihrist="kasa-ve-banka-toplami" deger={formatKisa(kasaTl)} tam={formatTl(kasaTl)} to="/kasa" soluk={kpi.kasaBanka.length === 0}
                   alt={<>
                     <div>{kpi.kasaBanka.length === 0 ? 'Henüz hesap tanımlı değil' : 'Kasalar ve banka hesapları'}</div>
                     {kasaDoviz && <div>{kasaDoviz}</div>}
                   </>} />
        )}
        <KpiKart ton="mavi" ikon="belge" etiket="Bu ay teklif" fihrist="bu-ay-teklif" deger={formatSayi(kpi.teklifAy)} to={can('quote.view') ? '/teklif' : undefined}
                 ipucu="Bu ay açılan teklif sayısı ve onaylananların oranı."
                 alt={<div>{kpi.teklifDonusPct == null ? 'Henüz teklif yok' : <span className="ccd-kpi-up">%{kpi.teklifDonusPct} satışa döndü</span>}</div>} />
        <KpiKart ton="gri" ikon="fabrika" etiket="Fasonda bekleyen" fihrist="fason-bekleyen" deger={`${formatSayi(kpi.fasonBekleyen)} iş`} to={can('fason.view') ? '/fason' : undefined} soluk={kpi.fasonBekleyen === 0}
                 ipucu="Fasoncuya gönderilmiş, henüz bitmemiş iş emirleri." alt={<div>Gönderildi · kabul · başladı</div>} />
      </KpiIzgara>

      {/* Vade kartları: yalnız çekleri / kredileri görebilen role gelir; gayriresmî olanlar yalnız görebilene dahildir. */}
      {(kpi.cekVade || kpi.krediAy) && (
        <div className={`ccd-kpi-izgara${kpi.cekVade && kpi.krediAy ? ' ccd-kpi-izgara-2' : ' ccd-kpi-izgara-1'}`}>
          {kpi.cekVade && (
            <KpiKart ton={kpi.cekVade.gecmisAdet > 0 ? 'kirmizi' : 'turuncu'} ikon="takvim" etiket="Vadesi yaklaşan çek / senet" fihrist="cek-vadesi-yaklasan" deger={`${formatSayi(kpi.cekVade.adet)} adet`}
                     to="/cek-senet?sekme=takvim" soluk={kpi.cekVade.adet === 0}
                     alt={kpi.cekVade.adet === 0 ? <div>Önümüzdeki {kpi.cekVade.gun} günde vadesi gelen yok</div> : <>
                       <div>{paraSatiri(kpi.cekVade.giris) ? `Giriş ${paraSatiri(kpi.cekVade.giris)}` : ''}{paraSatiri(kpi.cekVade.giris) && paraSatiri(kpi.cekVade.cikis) ? ' · ' : ''}{paraSatiri(kpi.cekVade.cikis) ? `Çıkış ${paraSatiri(kpi.cekVade.cikis)}` : ''}</div>
                       <div>{kpi.cekVade.gecmisAdet > 0 ? <b>{kpi.cekVade.gecmisAdet} tanesinin vadesi geçti</b> : `${kpi.cekVade.gun} gün içinde vadesi gelenler`}</div>
                     </>} />
          )}
          {kpi.krediAy && (
            <KpiKart ton="mor" ikon="terazi" etiket="Bu ay kredi taksiti" fihrist="kredi-bu-ay" deger={formatKisa(krediTl?.toplamKurus ?? 0)} tam={formatTl(krediTl?.toplamKurus ?? 0)} to="/kredi" soluk={kpi.krediAy.length === 0}
                     alt={kpi.krediAy.length === 0 ? <div>Bu ay kredi taksiti yok</div> : <>
                       <div>{krediTl ? (krediTl.kalanAdet === 0 ? `${krediTl.adet} taksitin hepsi ödendi` : `${krediTl.kalanAdet} taksit kaldı · ${formatTl(krediTl.kalanKurus)}`) : 'TL kredi taksiti yok'}</div>
                       {krediDoviz && <div>{krediDoviz}</div>}
                     </>} />
          )}
        </div>
      )}

      <div className="ccd-banner ccd-yazdirma-gizli" data-tur="ucta-uca">
        <span className="ccd-banner-icon" aria-hidden="true">▶</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Paneli baştan sona görmek ister misiniz?</div>
          <div className="ccd-meta">Uçtan uca tur: müşteriden tahsilata, alış faturasından stoğa — 12 adım. Sağ üstteki ⓘ ise her ekranın kılavuzu.</div>
        </div>
        <Link className="ccd-btn ccd-btn-accent ccd-btn-sm" to="/?tur=akis">Uçtan uca tur</Link>
      </div>

      <>
        <div className="ccd-card ccd-card-flush">
          <div className="ccd-card-header">
            <h2 className="ccd-heading">Eşik altı kalemler <Ipucu id="esik" /></h2>
            <span className="ccd-meta">{faturasizVar ? 'resmî + faturasız toplam' : 'resmî hane'}</span>
          </div>
          {veri.kritikStok.length === 0 ? (
            <div className="ccd-empty">Eşik altına inen kalem yok.</div>
          ) : (
            <div className="ccd-table-wrap ccd-rapor-tablo ccd-rapor-tablo-ic">
              <table className="ccd-table">
                <thead>
                  <tr>
                    <th className="ccd-th">Kod</th>
                    <th className="ccd-th">Ürün</th>
                    <th className="ccd-th ccd-td-num">Resmî <Ipucu id="resmi-stok" /></th>
                    {faturasizVar && <th className="ccd-th ccd-td-num">Faturasız <Ipucu id="faturasiz-stok" /></th>}
                    <th className="ccd-th ccd-td-num">Toplam <Ipucu id="toplam-stok" /></th>
                    <th className="ccd-th ccd-td-num">Eşik <Ipucu id="esik" /></th>
                  </tr>
                </thead>
                <tbody>
                  {veri.kritikStok.map((r) => (
                    <tr className="ccd-tr" key={r.id}>
                      <td className="ccd-td ccd-td-nowrap ccd-mono">{r.code}</td>
                      <td className="ccd-td">{r.name}</td>
                      <td className="ccd-td ccd-td-num ccd-kanal-official">{r.official}</td>
                      {faturasizVar && <td className="ccd-td ccd-td-num ccd-kanal-offrec">{r.offrec}</td>}
                      <td className="ccd-td ccd-td-num"><span className="ccd-badge ccd-badge-danger">{r.official + (r.offrec ?? 0)}</span></td>
                      <td className="ccd-td ccd-td-num ccd-muted">{r.threshold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="ccd-card">
          <div className="ccd-card-header" style={{ padding: 0, paddingBottom: 12, marginBottom: 12 }}>
            <h2 className="ccd-heading">Son hareketler <Ipucu id="stok-hareketi" /></h2>
            {!faturasizVar && <span className="ccd-meta">resmî hareketler</span>}
          </div>
          {veri.sonHareketler.length === 0 ? (
            <div className="ccd-empty">Henüz hareket yok.</div>
          ) : (
            <div className="ccd-timeline">
              {veri.sonHareketler.slice(0, 6).map((h) => (
                <div className="ccd-tl-item" key={h.id}>
                  <span className="ccd-tl-dot" />
                  <div className="ccd-tl-body">
                    <div className="ccd-tl-head">
                      <span className="ccd-tl-text"><span className="ccd-mono">{h.code}</span> · {h.name}</span>
                      <span className="ccd-tl-time">{formatTarih(h.at)}</span>
                    </div>
                    <div className="ccd-meta">
                      <span className={h.qty < 0 ? 'ccd-kpi-down' : 'ccd-kpi-up'}>{h.qty > 0 ? '+' : ''}{h.qty}</span>{' '}
                      · {CHANNEL_LABEL[h.channel]} · {MOVE_REASON_LABEL[h.reason]}
                      {h.note ? ` · ${h.note}` : ''} · <span className="ccd-tl-actor">{h.by}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    </>
  );
}
